import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  ArrowLeft, User, CreditCard, Palette, Loader2, CheckCircle, FileText, Gauge, AlertTriangle, Mail
} from 'lucide-react';
import { COLOR_SCHEMES, STYLE_PACKS, PRICING_TIERS } from '@/lib/constants';
import { authAPI, paymentsAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import Footer from '@/components/Footer';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // Track which plan is loading
  const [billingLoading, setBillingLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    company: user?.company || '',
  });

  const [prefsForm, setPrefsForm] = useState({
    default_color_scheme: (user?.default_color_scheme && COLOR_SCHEMES[user.default_color_scheme]) ? user.default_color_scheme : 'slate',
    default_style_pack: (user?.default_style_pack && STYLE_PACKS[user.default_style_pack]) ? user.default_style_pack : 'modern',
  });

  const [emailSettings, setEmailSettings] = useState({
    sender_name: user?.email_settings?.sender_name || '',
    reply_to: user?.email_settings?.reply_to || '',
    cc_self: user?.email_settings?.cc_self !== false,
  });

  const [evmThresholds, setEvmThresholds] = useState({
    good: user?.evm_rag_thresholds?.good || 0.95,
    ok: user?.evm_rag_thresholds?.ok || 0.85,
  });

  const isPaidUser = true; // All features available for all users

  // Handle payment return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');

    if (sessionId && paymentStatus === 'success') {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      setError('Payment was cancelled. You can try again anytime.');
      // Clear the URL params
      setSearchParams({});
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setError('Payment status check timed out. Please refresh the page to see your updated subscription.');
      setSearchParams({});
      return;
    }

    setLoading(true);
    try {
      const response = await paymentsAPI.getPaymentStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setSuccess(`🎉 Successfully upgraded to ${data.tier} tier!`);
        // Refresh user data
        const userResponse = await authAPI.getMe();
        await updateUser(userResponse.data);
        setSearchParams({});
        setLoading(false);
        return;
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setSearchParams({});
        setLoading(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      setError('Error checking payment status. Please refresh the page.');
      setSearchParams({});
      setLoading(false);
    }
  };

  const handleCheckout = async (tier) => {
    const packageId = `${tier}_${billingCycle}`;
    setCheckoutLoading(tier);
    setError('');

    try {
      const response = await paymentsAPI.createCheckout(packageId);
      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setError('');
    try {
      await authAPI.cancelSubscription();
      updateUser({ ...user, tier: 'trial', subscription_status: 'cancelled' });
      setSuccess('Subscription cancelled. You have access until the end of your billing period.');
      setShowCancelDialog(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    setError('');
    try {
      const response = await paymentsAPI.createPortalSession();
      window.location.href = response.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to open billing portal');
      setBillingLoading(false);
    }
  };

  const handleProfileSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateUser(profileForm);
      setSuccess('Profile updated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePrefsSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateUser({
        ...prefsForm,
        evm_rag_thresholds: evmThresholds,
      });
      setSuccess('Preferences updated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSettingsSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await updateUser({
        email_settings: {
          sender_name: emailSettings.sender_name || null,
          reply_to: emailSettings.reply_to || null,
          cc_self: emailSettings.cc_self,
        },
      });
      setSuccess('Email settings updated successfully');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update email settings');
    } finally {
      setLoading(false);
    }
  };

  const tierInfo = PRICING_TIERS[user?.tier || 'trial'];

  // Helper to get RAG color preview
  const getRAGColor = (value) => {
    if (value >= evmThresholds.good) return 'bg-emerald-500';
    if (value >= evmThresholds.ok) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Calculate yearly savings
  const getYearlySavings = (monthlyPrice) => {
    const yearlyTotal = monthlyPrice * 12;
    const discountedYearly = monthlyPrice * 10; // 2 months free
    return Math.round(yearlyTotal - discountedYearly);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold">Settings</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="email">Email</TabsTrigger>
            <TabsTrigger value="subscription">Subscription</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Profile Information
                </CardTitle>
                <CardDescription>Update your personal information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={user?.email || ''}
                    disabled
                    className="bg-slate-50"
                  />
                  <p className="text-xs text-slate-500">Email cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    placeholder="Your name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Company (optional)</Label>
                  <Input
                    id="company"
                    value={profileForm.company}
                    onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })}
                    placeholder="Your company"
                  />
                </div>

                <Button onClick={handleProfileSave} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Default Preferences
                </CardTitle>
                <CardDescription>Set your default report styling</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Default Color Scheme</Label>
                  <Select
                    value={prefsForm.default_color_scheme}
                    onValueChange={(value) => setPrefsForm({ ...prefsForm, default_color_scheme: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(COLOR_SCHEMES).map(([key, scheme]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 rounded" style={{ backgroundColor: scheme.primary }} />
                            <span>{scheme.name}</span>
                            <span className="text-xs text-slate-500">— {scheme.vibe}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Default Style Pack</Label>
                  <Select
                    value={prefsForm.default_style_pack}
                    onValueChange={(value) => setPrefsForm({ ...prefsForm, default_style_pack: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STYLE_PACKS).map(([key, pack]) => (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <span>{pack.name}</span>
                            <span className="text-xs text-slate-500">— {pack.vibe}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: (COLOR_SCHEMES[prefsForm.default_color_scheme] || COLOR_SCHEMES.slate).background }}>
                  <div className="p-4 rounded" style={{ backgroundColor: (COLOR_SCHEMES[prefsForm.default_color_scheme] || COLOR_SCHEMES.slate).primary }}>
                    <h3 className="text-white font-bold" style={{ fontFamily: (STYLE_PACKS[prefsForm.default_style_pack] || STYLE_PACKS.modern).headlines }}>
                      Preview: {(COLOR_SCHEMES[prefsForm.default_color_scheme] || COLOR_SCHEMES.slate).name} + {(STYLE_PACKS[prefsForm.default_style_pack] || STYLE_PACKS.modern).name}
                    </h3>
                  </div>
                </div>

                <Separator className="my-6" />

                {/* EVM RAG Thresholds */}
                <div className="space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Gauge className="h-4 w-4" />
                    EVM Performance Thresholds
                  </h3>
                  <p className="text-sm text-slate-500">
                    Customize when SPI/CPI indicators show green, amber, or red.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="threshold-good">Green (Good) Threshold</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="threshold-good"
                          type="number"
                          step="0.01"
                          min="0"
                          max="2"
                          value={evmThresholds.good}
                          onChange={(e) => setEvmThresholds({ ...evmThresholds, good: parseFloat(e.target.value) || 0 })}
                          className="w-24"
                        />
                        <span className="text-sm text-slate-500">and above</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="threshold-ok">Amber (OK) Threshold</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="threshold-ok"
                          type="number"
                          step="0.01"
                          min="0"
                          max="2"
                          value={evmThresholds.ok}
                          onChange={(e) => setEvmThresholds({ ...evmThresholds, ok: parseFloat(e.target.value) || 0 })}
                          className="w-24"
                        />
                        <span className="text-sm text-slate-500">to {evmThresholds.good}</span>
                      </div>
                    </div>
                  </div>

                  {/* RAG Preview */}
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium mb-3">Preview:</p>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${getRAGColor(evmThresholds.good)}`}></div>
                        <span className="text-sm">≥ {evmThresholds.good}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded-full ${getRAGColor(evmThresholds.ok)}`}></div>
                        <span className="text-sm">{evmThresholds.ok} - {evmThresholds.good}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500"></div>
                        <span className="text-sm">&lt; {evmThresholds.ok}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button onClick={handlePrefsSave} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Preferences
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Tab */}
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Settings
                </CardTitle>
                <CardDescription>Configure how your report emails are sent</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sender Name */}
                <div className="space-y-2">
                  <Label htmlFor="sender_name">Sender Name</Label>
                  <Input
                    id="sender_name"
                    value={emailSettings.sender_name}
                    onChange={(e) => setEmailSettings({ ...emailSettings, sender_name: e.target.value })}
                    placeholder={user?.name || 'Your name'}
                  />
                  <p className="text-sm text-slate-500">
                    The name that appears in the &quot;From&quot; field when you send reports.
                    Defaults to your profile name if not set.
                  </p>
                </div>

                {/* Reply-To */}
                <div className="space-y-2">
                  <Label htmlFor="reply_to">Reply-To Email</Label>
                  <Input
                    id="reply_to"
                    type="email"
                    value={emailSettings.reply_to}
                    onChange={(e) => setEmailSettings({ ...emailSettings, reply_to: e.target.value })}
                    placeholder={user?.email || 'your@email.com'}
                  />
                  <p className="text-sm text-slate-500">
                    When recipients reply to your report emails, replies go to this address.
                    Defaults to your account email if not set.
                  </p>
                </div>

                <Separator />

                {/* CC Self */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">CC Myself on Sent Emails</Label>
                    <p className="text-sm text-slate-500">
                      Receive a copy of every report email you send.
                    </p>
                  </div>
                  <Switch
                    checked={emailSettings.cc_self}
                    onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, cc_self: checked })}
                  />
                </div>

                <Button onClick={handleEmailSettingsSave} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save Email Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscription
                </CardTitle>
                <CardDescription>Manage your subscription plan</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Plan */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Current Plan</p>
                      <p className="text-xl font-bold">{tierInfo.name}</p>
                      {user?.billing_cycle && user?.tier !== 'trial' && (
                        <p className="text-sm text-slate-500">
                          Billed {user.billing_cycle}
                        </p>
                      )}
                    </div>
                    <Badge variant={user?.tier === 'trial' ? 'secondary' : 'default'}>
                      {user?.tier === 'trial' ? 'Trial' : 'Active'}
                    </Badge>
                  </div>
                  {user?.tier === 'trial' && user?.trial_ends_at && (
                    <p className="text-sm text-slate-500 mt-2">
                      Trial ends: {formatDate(user.trial_ends_at)}
                    </p>
                  )}
                </div>

                <Separator />

                {/* Upgrade Options */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">Available Plans</h3>
                    {/* Billing Cycle Toggle */}
                    <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                      <button
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          billingCycle === 'monthly' 
                            ? 'bg-white shadow-sm font-medium' 
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        onClick={() => setBillingCycle('monthly')}
                      >
                        Monthly
                      </button>
                      <button
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          billingCycle === 'yearly' 
                            ? 'bg-white shadow-sm font-medium' 
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                        onClick={() => setBillingCycle('yearly')}
                      >
                        Yearly
                        <span className="ml-1 text-xs text-green-600 font-medium">Save 17%</span>
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-4">
                    {Object.entries(PRICING_TIERS).filter(([key]) => key !== 'trial').map(([key, tier]) => {
                      const isCurrentPlan = user?.tier === key;
                      const monthlyPrice = tier.monthlyPrice;
                      const yearlyPrice = Math.round(monthlyPrice * 10); // 2 months free
                      const displayPrice = billingCycle === 'yearly' ? yearlyPrice : monthlyPrice;
                      const priceLabel = billingCycle === 'yearly' ? '/year' : '/month';
                      const isLoading = checkoutLoading === key;

                      return (
                        <div key={key} className={`p-4 border rounded-lg transition-all ${
                          isCurrentPlan ? 'border-blue-500 bg-blue-50' : 'hover:border-slate-300'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold">{tier.name}</p>
                                {billingCycle === 'yearly' && (
                                  <Badge variant="secondary" className="text-green-700 bg-green-100">
                                    Save ${getYearlySavings(monthlyPrice)}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-2xl font-bold">
                                ${displayPrice}
                                <span className="text-sm font-normal text-slate-500">{priceLabel}</span>
                              </p>
                              {billingCycle === 'yearly' && (
                                <p className="text-xs text-slate-500">
                                  ${Math.round(yearlyPrice / 12)}/month when billed yearly
                                </p>
                              )}
                              <ul className="text-sm text-slate-600 mt-2">
                                {tier.features.map((f, i) => (
                                  <li key={i} className="flex items-center gap-1">
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                    {f}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <Button
                              variant={isCurrentPlan ? 'secondary' : 'default'}
                              disabled={isCurrentPlan || isLoading || loading}
                              onClick={() => handleCheckout(key)}
                              className="min-w-[100px]"
                              data-testid={`checkout-${key}-btn`}
                            >
                              {isLoading ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  Loading...
                                </>
                              ) : isCurrentPlan ? (
                                'Current'
                              ) : (
                                'Subscribe'
                              )}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <p className="text-xs text-slate-500 text-center">
                    Secure payment powered by Stripe. Cancel anytime.
                  </p>
                </div>

                <Separator />

                {/* Account Actions */}
                <div className="space-y-4">
                  <h3 className="font-semibold">Account</h3>
                  
                  {/* Manage Billing - only show for paid users */}
                  {isPaidUser && (
                    <div className="p-4 border rounded-lg bg-slate-50">
                      <h4 className="font-medium mb-2">Billing</h4>
                      <p className="text-sm text-slate-500 mb-3">
                        View invoices, update payment method, or manage subscription
                      </p>
                      <Button 
                        variant="outline" 
                        onClick={handleManageBilling}
                        disabled={billingLoading}
                        data-testid="manage-billing-btn"
                      >
                        {billingLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <CreditCard className="h-4 w-4 mr-2" />
                            Manage Billing
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Cancel Subscription - only show for paid users */}
                  {isPaidUser && (
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-red-800">Cancel Subscription</p>
                          <p className="text-sm text-red-600 mt-1">
                            You will have read-only access for 90 days after cancellation. 
                            You can export your data during this period.
                          </p>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            className="mt-3"
                            onClick={() => setShowCancelDialog(true)}
                          >
                            Cancel Subscription
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Button variant="outline" onClick={logout}>
                    Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Cancel Subscription Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel your subscription?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3 text-sm">
            <p><strong>What happens when you cancel:</strong></p>
            <ul className="list-disc list-inside space-y-1 text-slate-600">
              <li>Your subscription will remain active until the end of your billing period</li>
              <li>After that, you&apos;ll have 90 days of read-only access to your projects</li>
              <li>You can export all your data during this period</li>
              <li>After 90 days, your data will be permanently deleted</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Subscription
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelSubscription}
              disabled={cancelling}
            >
              {cancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes, Cancel Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default SettingsPage;
